// GA4 (gtag.js) wrapper. Measurement ID comes from REACT_APP_GA4_ID (public, not a secret).
const GA_ID = process.env.REACT_APP_GA4_ID;
let inited = false;

// --- Internal-traffic flagging ------------------------------------------------
// A per-browser flag that marks this device as the studio owner's / a testing device.
// Flagged devices send `traffic_type: 'internal'` on every GA4 event so the owner can
// Exclude them with a GA4 "Internal Traffic" Data Filter. This is IP-independent
// (travel-safe) and NEVER touches ordinary (unflagged) visitors. The flag is set only
// from the authenticated /admin area — there is no public URL parameter that could
// accidentally flag a real visitor from a shared link.
const INTERNAL_KEY = "cs_internal"; // "1" = internal, "0" = explicitly excluded, null = untouched

export function isInternalDevice() {
  try { return localStorage.getItem(INTERNAL_KEY) === "1"; } catch { return false; }
}

// Returns true only if the owner has never made a choice on this browser yet.
export function internalFlagUnset() {
  try { return localStorage.getItem(INTERNAL_KEY) === null; } catch { return false; }
}

export function setInternalDevice(on) {
  try { localStorage.setItem(INTERNAL_KEY, on ? "1" : "0"); } catch {}
  // Apply immediately to a live GA instance (no reload needed) so DebugView reflects it.
  if (gaReady()) window.gtag("config", GA_ID, { send_page_view: false, traffic_type: on ? "internal" : undefined });
}

function onAdminPath() {
  try { return window.location.pathname.startsWith("/admin"); } catch { return false; }
}

// Merge traffic_type:'internal' into any event params when this device is flagged.
function tagged(params = {}) {
  return isInternalDevice() ? { ...params, traffic_type: "internal" } : params;
}

export function initGA() {
  if (inited || !GA_ID) return;
  inited = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, tagged({ send_page_view: false }));
}

// Defer GA4 off the critical render path (mirrors the PostHog deferral in index.html): init on
// browser idle OR first user interaction, whichever comes first. Keeps gtag.js (~175KB) out of the
// initial main-thread trace to cut TBT, while still capturing the landing page_view once ready.
let scheduled = false;
export function scheduleGA() {
  if (scheduled || !GA_ID) return;
  // Suppress GA4 entirely on /admin routes — CMS administration is never counted in site analytics.
  if (onAdminPath()) return;
  scheduled = true;
  let fired = false;
  const evs = ["pointerdown", "keydown", "scroll"];
  const cleanup = () => evs.forEach((e) => window.removeEventListener(e, go));
  function go() {
    if (fired) return;
    fired = true;
    cleanup();
    initGA();
    trackPageView(window.location.pathname); // landing view was skipped before GA was ready
  }
  function schedule() {
    if ("requestIdleCallback" in window) window.requestIdleCallback(go, { timeout: 4000 });
    else setTimeout(go, 2500);
    evs.forEach((e) => window.addEventListener(e, go, { once: true, passive: true }));
  }
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}

export function gaReady() {
  return Boolean(GA_ID && typeof window.gtag === "function");
}

let _lastPV = { path: null, t: 0 };
export function trackPageView(pathname) {
  if (!gaReady()) return;
  if (onAdminPath()) return; // never record pageviews while in the CMS
  const t = Date.now();
  if (_lastPV.path === pathname && t - _lastPV.t < 2000) return; // dedupe StrictMode/double-mount
  _lastPV = { path: pathname, t };
  window.gtag("event", "page_view", tagged({
    page_title: document.title,
    page_location: window.location.origin + pathname + window.location.search,
  }));
}

export function trackEvent(name, params = {}) {
  if (onAdminPath()) return;
  if (gaReady()) window.gtag("event", name, tagged(params));
}

const num = (v) => Number(v) || 0;

let _lastView = { id: null, t: 0 };
export function gaViewItem(p) {
  const t = Date.now();
  if (_lastView.id === p.slug && t - _lastView.t < 2000) return; // dedupe StrictMode/double-mount
  _lastView = { id: p.slug, t };
  trackEvent("view_item", {
    currency: p.currency || "EUR",
    value: num(p.price),
    items: [{ item_id: p.slug, item_name: p.title, price: num(p.price), quantity: 1 }],
  });
}

export function gaAddToCart(item) {
  const price = num(item.price);
  const qty = item.quantity || 1;
  trackEvent("add_to_cart", {
    currency: "EUR",
    value: price * qty,
    items: [{ item_id: item.product_slug || item.kind, item_name: item.title || "Custom Etching", price, quantity: qty }],
  });
}

export function gaBeginCheckout(items, value) {
  trackEvent("begin_checkout", {
    currency: "EUR",
    value: num(value),
    items: items.map((i) => ({ item_id: i.product_slug || i.kind, item_name: i.title || "Custom Etching", price: num(i.price), quantity: i.quantity || 1 })),
  });
}

export function gaPurchase(order) {
  trackEvent("purchase", {
    transaction_id: String(order.order_id),
    currency: "EUR",
    value: num(order.value),
    shipping: num(order.shipping),
    items: order.items || [],
  });
}
